import { useMsal } from "@azure/msal-react";
import { Checkbox, DefaultButton, Dropdown, IDropdownOption, IPanelProps, IRenderFunction, Link, Panel, PanelType, PrimaryButton, Separator, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import { RootState } from "app/store";
import saveAs from "file-saver";
import { compareUnderWritingReviewToWithoutDispatch, emitCustomEvent, fadeIn, panelButtonsStyles } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { IEntityReferenceValue } from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useSelector } from 'react-redux';
import SnapshotLookup from "../Controls/SnapshotLookup";
import { Constants } from "global/Application/Constants";

export interface IProps extends TComponentConnectedProps {
    isOpen: boolean;

    // dialog closure request from parent component
    onDismiss?: () => void
}

interface IViewModel {
    leftSnapshotRef: IEntityReferenceValue | undefined;
    leftBlobVersion: string | undefined;
    rightSnapshotRef: IEntityReferenceValue | undefined;
    rightBlobVersion: string | undefined;
    accountRef: IEntityReferenceValue | undefined;
    output: string;
    release: string;
    tier: string;
    attribute: string;
    entities: string[];
    deactivated: string;
    fallback: string;
    pickVersions: boolean;
    useCurrentSnapshot: boolean;
    mountingKey: number;
    generateSingleSheet: boolean;
};


const CompareExtractWizard = (props: IProps) => {

    const { instance } = useMsal();
    const state = useSelector((state: RootState) => state);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const options = useMemo(() => {
        const entities: IDropdownOption[] = [
            { key: 'groupConfiguration', text: 'Group Configuration' },
            { key: 'plan', text: 'Plan' },
            { key: 'benefit', text: 'Benefit' },
            { key: 'accountReceivable', text: 'Account Receivable' },
            { key: 'feesAndPremium', text: 'Fees AndPremium' },
            { key: 'stopLoss', text: 'Stop Loss' },
        ];


        const attribute: IDropdownOption[] = [
            { key: 'masterAttribute', text: 'By Master Attribute' },
            { key: 'attribute', text: 'By Attribute' },
        ];

        const tier: IDropdownOption[] = [
            { key: 'default', text: 'By Default Level Name' },
            { key: 'user', text: 'By User Level Name' },
        ];

        const release: IDropdownOption[] = [
            { key: 'release', text: 'Use Each Snapshot\'s Release' },
            { key: 'lastPublished', text: 'Use Published Release' },
        ];

        const deactivated: IDropdownOption[] = [
            { key: 'fieldByField', text: 'Compare Field By Field' },
            { key: 'blankDeactivated', text: 'Blank Fields Under' }, // and remove both side if deactivated
            { key: 'ignore', text: 'Ignore Compare If On Either Side' },
        ];

        const output: IDropdownOption[] = [
            { key: 'excel', text: 'Excel Spreadsheet' },
            { key: 'html', text: 'Web Report/HTML' }, // and remove both side if deactivated
            { key: 'csv', text: 'CSV' },
        ];

        return {
            entities,
            attribute,
            tier,
            release,
            deactivated,
            output,
        };

    }, []);

    const getCurrentSnapshotRef = () => {
        return {
            entityType: 'bcbs_groupconfigurationsnapshot',
            type: 'entity',
            id: state.data.snapshot.bcbs_groupconfigurationsnapshotid!,
            name: state.data.snapshot.bcbs_name!
        } as IEntityReferenceValue;
    }

    const resetViewModel = () => {
        const viewModel: IViewModel = {
            leftSnapshotRef: suggestedSnapshot
                ? {
                    entityType: 'bcbs_groupconfigurationsnapshot',
                    type: 'entity',
                    id: suggestedSnapshot.bcbs_groupconfigurationsnapshotid!,
                    name: suggestedSnapshot.bcbs_name!,
                }
                : undefined,
            leftBlobVersion: undefined,
            rightSnapshotRef: getCurrentSnapshotRef(),
            rightBlobVersion: undefined,
            accountRef: {
                id: state.data.account?.accountid!,
                name: state.data.account?.name!,
                entityType: 'account',
                type: 'entity',
            },
            output: 'excel',
            release: 'release',
            tier: 'default',
            attribute: 'masterAttribute',
            entities: [
                'groupConfiguration',
                'plan',
                'benefit',
                'accountReceivable',
                'feesAndPremium',
                'stopLoss',
            ],
            deactivated: 'fieldByField',
            fallback: 'name',
            pickVersions: false,
            useCurrentSnapshot: false,
            mountingKey: 1,
            generateSingleSheet: false,
        };

        return viewModel;
    };

    useEffect(() => {
        if (props.isOpen) {
            setViewModel(resetViewModel());
        }

    }, [props.isOpen]);


    const suggestedSnapshot = useMemo(() => {
        return compareUnderWritingReviewToWithoutDispatch(state, state.data.snapshot);
    }, []);

    const [viewModel, setViewModel] = useState<IViewModel>(resetViewModel());

    const [loading, setLoading] = useState<boolean>(false);

    const onRenderAdvancedOptions = () => {
        return <>

            <Checkbox
                label="Use current BlueRelationship"
                checked={!!viewModel.accountRef}
                onChange={(event, checked) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            accountRef: !xViewModel.accountRef
                                ? {
                                    id: state.data.account?.accountid!,
                                    name: state.data.account?.name!,
                                    entityType: 'account',
                                    type: 'entity',
                                } as IEntityReferenceValue
                                : undefined
                        }
                    });
                }}
            />

            {state.status.debug.admin &&
                <>
                    <Checkbox
                        label="Use current configuration"
                        checked={!!viewModel.useCurrentSnapshot}
                        onChange={(event, checked) => {
                            setViewModel(xViewModel => {
                                return {
                                    ...xViewModel,
                                    useCurrentSnapshot: checked ?? false,
                                    rightSnapshotRef: checked ? getCurrentSnapshotRef() : undefined,
                                    leftSnapshotRef: checked ? getCurrentSnapshotRef() : undefined,
                                    mountingKey: xViewModel.mountingKey + 1,
                                }
                            });
                        }}
                    />
                    <Checkbox
                        label="Pick versions"
                        checked={!!viewModel.pickVersions}
                        onChange={(event, checked) => {
                            setViewModel(xViewModel => {
                                let newViewModel: typeof viewModel = {
                                    ...xViewModel,
                                    pickVersions: !xViewModel.pickVersions
                                };

                                if (!newViewModel.pickVersions)
                                    newViewModel = {
                                        ...newViewModel,
                                        leftBlobVersion: undefined,
                                        rightBlobVersion: undefined,
                                        mountingKey: xViewModel.mountingKey + 1,
                                    };

                                return newViewModel;
                            });
                        }}
                    />
                </>

            }


            <Dropdown
                selectedKey={viewModel.output}
                disabled={true}
                label="Output"
                options={options.output}
                onChange={(event, option) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            output: option?.key as string
                        }
                    });
                }}
            />

            <Dropdown
                label="Deactivated Context"
                selectedKey={viewModel.deactivated}
                disabled={true}
                options={options.deactivated}
                onChange={(event, option) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            deactivated: option?.key as string
                        }
                    });
                }}
            />

            <Dropdown
                label="Entities"
                selectedKeys={viewModel.entities}
                disabled={true}
                multiSelect={true}
                options={options.entities}
                onChange={(event, option) => {

                    setViewModel(xViewModel => {

                        let entities = [...xViewModel.entities];

                        if (option && option.selected === true)
                            entities.push(option.key as string);

                        if (option && option.selected === false)
                            entities = entities.filter(q => q !== option.key as string)

                        return {
                            ...xViewModel, entities: entities
                        }
                    });


                }}
            />

            <Dropdown
                label="Release"
                selectedKey={viewModel.release}
                disabled={true}
                options={options.release}
                onChange={(event, option) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            release: option?.key as string
                        }
                    });
                }}
            />

            <Dropdown
                label="Tier Match"
                selectedKey={viewModel.tier}
                disabled={true}
                options={options.tier}
                onChange={(event, option) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            tier: option?.key as string
                        }
                    });
                }}
            />

            <Dropdown
                label="Field Match"
                disabled={true}
                selectedKey={viewModel.attribute}
                options={options.attribute}
                onChange={(event, option) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            attribute: option?.key as string
                        }
                    });
                }}
            />

            <Checkbox
                label="Fallback to name, if no match by id"
                checked={viewModel.fallback === 'name'}

                disabled={true}
                onChange={(event, checked) => {
                    setViewModel(xViewModel => {
                        return {
                            ...xViewModel,
                            fallback: xViewModel.fallback === 'name' ? '' : 'name'
                        }
                    });
                }}
            />

        </>;
    }


    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const onRenderBodyLoading = () => {
        return <Spinner
            label="Please wait..."
            ariaLive="assertive"
            labelPosition="right"
            size={SpinnerSize.large}
            styles={{
                root: { padding: '25px' }
            }}
        />;
    }

    const onRenderBody = () => {
        return <div className={fadeIn}>
            <Separator />

            <Stack tokens={{ childrenGap: '15px' }}>
                {loading &&
                    onRenderBodyLoading()
                }
                {!loading && <>
                    <SnapshotLookup
                        key={viewModel.mountingKey + "A"}
                        searchOnMount={false}
                        label={'Left Snapshot'}
                        defaultValueAccount={viewModel.accountRef}
                        defaultValueSnapshot={viewModel.leftSnapshotRef}
                        defaultValueBlobVersion={viewModel.leftBlobVersion}
                        valueAccount={viewModel.accountRef}
                        // valueSnapshot={viewModel.leftSnapshotRef}
                        searchBlobVersion={viewModel.pickVersions}
                        onPick={(value, version) => {
                            if (value)
                                setViewModel(xViewModel => {
                                    return {
                                        ...xViewModel,
                                        leftSnapshotRef: value,
                                        leftBlobVersion: version ?? undefined,
                                    }
                                });
                        }}
                        onCancel={() => {
                            setViewModel(xViewModel => {
                                return {
                                    ...xViewModel,
                                    mountingKey: xViewModel.mountingKey + 1,
                                }
                            });
                        }}
                    />

                    <SnapshotLookup
                        key={viewModel.mountingKey + "B"}
                        searchOnMount={false}
                        label={'Right Snapshot'}
                        defaultValueAccount={viewModel.accountRef}
                        defaultValueSnapshot={viewModel.rightSnapshotRef}
                        defaultValueBlobVersion={viewModel.leftBlobVersion}
                        valueAccount={viewModel.accountRef}
                        // valueSnapshot={viewModel.rightSnapshotRef}
                        searchBlobVersion={viewModel.pickVersions}
                        onPick={(value, version) => {
                            if (value)
                                setViewModel(xViewModel => {
                                    return {
                                        ...xViewModel,
                                        rightSnapshotRef: value,
                                        rightBlobVersion: version ?? undefined,
                                    }
                                });
                        }}
                        onCancel={() => {
                            setViewModel(xViewModel => {
                                return {
                                    ...xViewModel,
                                    rightSnapshotRef: undefined,
                                    rightBlobVersion: undefined,
                                    mountingKey: xViewModel.mountingKey + 1,
                                }
                            });
                        }}
                    />

                    <Checkbox
                        label="Generate a single-sheet spreadsheet"
                        checked={!!viewModel.generateSingleSheet}
                        onChange={(event, checked) => {
                            setViewModel(xViewModel => {
                                return {
                                    ...xViewModel,
                                    generateSingleSheet: checked ?? false
                                }
                            });
                        }}

                    />

                    {showAdvanced &&
                        onRenderAdvancedOptions()
                    }

                    <Link
                        onClick={() => setShowAdvanced(t => !t)}
                        styles={{ root: { fontSize: 'small' } }}
                    >
                        {!showAdvanced &&
                            <>Show More Options</>
                        }
                        {showAdvanced &&
                            <>Hide More Options</>
                        }
                    </Link>
                </>
                }


            </Stack>
        </div>;
    }


    const onRenderFooter: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return <>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={loading || !viewModel.leftSnapshotRef?.id || !viewModel.rightSnapshotRef?.id}
                onClick={async () => {
                    try {

                        if (!viewModel.leftSnapshotRef?.id)
                            return;

                        if (!viewModel.rightSnapshotRef?.id)
                            return;

                        setLoading(true);

                        const response = await dataSource.getSnapshotCompareExtract(
                            {
                                bcbs_groupconfigurationsnapshotid: viewModel.leftSnapshotRef?.id,
                                blobVersion: viewModel.leftBlobVersion
                            },
                            {
                                bcbs_groupconfigurationsnapshotid: viewModel.rightSnapshotRef?.id,
                                blobVersion: viewModel.rightBlobVersion
                            },
                            viewModel.generateSingleSheet,
                            () => {
                                // TODO implement a progress status
                                console.log(Date.now())
                            },
                        );

                        saveAs(response.blob, response.fileName);

                    } catch (error: any) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });
                    } finally {
                        setLoading(false);
                    }

                }}

            />
            <DefaultButton text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => {
                    props.onDismiss?.();
                }}

            />
        </>;
    }

    return <Panel
        isOpen={props.isOpen}
        type={PanelType.medium}
        isLightDismiss={false}
        closeButtonAriaLabel="Close"
        headerText="Compare Extract"
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
        onDismiss={props.onDismiss}
    >
        {onRenderBody()}
    </Panel>


}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(CompareExtractWizard);


